import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useTemplate } from '@/contexts/TemplateContext'
import type { PublicProfile } from '@/types'
import { Gallery } from '@/components/gallery'
import { BookingModal } from '@/components/booking/BookingModal'

interface PersonalTrainerTemplateProps {
    profile: PublicProfile
}

export function PersonalTrainerTemplate({ profile }: PersonalTrainerTemplateProps) {
    const { t } = useTranslation('public')
    const { colors, typography } = useTemplate()
    const [isBookingOpen, setIsBookingOpen] = useState(false)

    return (
        <div
            className="min-h-screen"
            style={{
                fontFamily: typography.body,
                backgroundColor: colors.colors.background,
                color: colors.colors.text,
            }}
        >
            {/* Navigation */}
            <nav
                className="sticky top-0 z-50 backdrop-blur-md"
                style={{ backgroundColor: `${colors.colors.primary}E6` }}
            >
                <div className="max-w-7xl mx-auto px-4 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <div
                                className="w-12 h-12 rounded-full flex items-center justify-center"
                                style={{ backgroundColor: colors.colors.accent }}
                            >
                                <i className="fas fa-dumbbell text-white text-xl" />
                            </div>
                            <div>
                                <h1
                                    className="text-xl font-bold tracking-wide"
                                    style={{ fontFamily: typography.heading, color: colors.colors.secondary }}
                                >
                                    {profile.business_name.toUpperCase()}
                                </h1>
                                {profile.tagline && (
                                    <p className="text-xs uppercase tracking-widest" style={{ color: colors.colors.accent }}>
                                        {profile.tagline}
                                    </p>
                                )}
                            </div>
                        </div>
                        <div className="hidden md:flex items-center space-x-8">
                            <a href="#about" className="transition-colors cursor-pointer" style={{ color: 'rgba(255,255,255,0.7)' }}>
                                {t('template.trainer.about')}
                            </a>
                            <a href="#services" className="transition-colors cursor-pointer" style={{ color: 'rgba(255,255,255,0.7)' }}>
                                {t('template.trainer.programs')}
                            </a>
                            <a href="#testimonials" className="transition-colors cursor-pointer" style={{ color: 'rgba(255,255,255,0.7)' }}>
                                {t('template.trainer.results')}
                            </a>
                            <a href="#contact" className="transition-colors cursor-pointer" style={{ color: 'rgba(255,255,255,0.7)' }}>
                                {t('template.trainer.contact')}
                            </a>
                            <button
                                onClick={() => setIsBookingOpen(true)}
                                className="px-6 py-3 rounded-full font-bold transition-colors cursor-pointer"
                                style={{ backgroundColor: colors.colors.accent, color: colors.colors.secondary }}
                            >
                                {t('template.trainer.startTraining')}
                            </button>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Hero */}
            <section className="relative py-20 overflow-hidden" style={{ backgroundColor: colors.colors.primary }}>
                <div className="absolute inset-0 opacity-20">
                    <div
                        className="absolute top-20 right-20 w-96 h-96 rounded-full blur-3xl"
                        style={{ backgroundColor: colors.colors.accent }}
                    />
                    <div
                        className="absolute bottom-20 left-20 w-96 h-96 rounded-full blur-3xl"
                        style={{ backgroundColor: colors.colors.secondary }}
                    />
                </div>
                <div className="max-w-7xl mx-auto px-4 relative z-10">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <p
                                className="uppercase tracking-widest mb-4 text-sm font-bold"
                                style={{ color: colors.colors.accent }}
                            >
                                {t('template.trainer.transformYourBody')}
                            </p>
                            <h1
                                className="text-5xl md:text-7xl font-bold leading-tight mb-6"
                                style={{ fontFamily: typography.heading, color: colors.colors.secondary }}
                            >
                                {t('template.trainer.unleashYourPotential')} <br />
                                <span
                                    className="text-transparent bg-clip-text"
                                    style={{
                                        backgroundImage: `linear-gradient(to right, ${colors.colors.accent}, ${colors.colors.secondary})`,
                                    }}
                                >
                                    {t('template.trainer.fullPotential')}
                                </span>
                            </h1>
                            <p className="text-xl mb-8 leading-relaxed" style={{ color: 'rgba(255,255,255,0.8)' }}>
                                {t('template.trainer.personalizedTraining')}
                            </p>
                            <div className="flex flex-wrap gap-4">
                                <a
                                    href="#contact"
                                    className="inline-flex items-center px-8 py-4 rounded-full font-bold transition-all shadow-lg"
                                    style={{
                                        backgroundColor: colors.colors.accent,
                                        color: colors.colors.secondary,
                                    }}
                                >
                                    {t('template.trainer.getStartedFree')}
                                </a>
                                <a
                                    href="#services"
                                    className="inline-flex items-center px-8 py-4 border-2 border-white text-white rounded-full font-bold transition-all"
                                >
                                    {t('template.trainer.viewPrograms')}
                                </a>
                            </div>
                            <div className="flex items-center space-x-8 mt-12">
                                <div>
                                    <p
                                        className="text-4xl font-bold"
                                        style={{ fontFamily: typography.heading, color: colors.colors.accent }}
                                    >
                                        500+
                                    </p>
                                    <p className="text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>{t('template.trainer.clientsTransformed')}</p>
                                </div>
                                <div>
                                    <p
                                        className="text-4xl font-bold"
                                        style={{ fontFamily: typography.heading, color: colors.colors.accent }}
                                    >
                                        12+
                                    </p>
                                    <p className="text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>{t('template.trainer.yearsExperience')}</p>
                                </div>
                                <div>
                                    <p
                                        className="text-4xl font-bold"
                                        style={{ fontFamily: typography.heading, color: colors.colors.accent }}
                                    >
                                        100%
                                    </p>
                                    <p className="text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>{t('template.trainer.onlineAvailable')}</p>
                                </div>
                            </div>
                        </div>
                        <div className="relative">
                            {profile.profile_image ? (
                                <img
                                    src={profile.profile_image}
                                    alt={profile.business_name}
                                    className="rounded-3xl shadow-2xl w-full object-cover"
                                />
                            ) : (
                                <div
                                    className="rounded-3xl shadow-2xl w-full aspect-square flex items-center justify-center"
                                    style={{ backgroundColor: colors.colors.secondary }}
                                >
                                    <span
                                        className="text-6xl font-bold"
                                        style={{ fontFamily: typography.heading, color: colors.colors.primary }}
                                    >
                                        {profile.business_name[0]}
                                    </span>
                                </div>
                            )}
                            <div
                                className="absolute -bottom-6 -left-6 rounded-2xl p-6 shadow-xl"
                                style={{ backgroundColor: colors.colors.accent, color: colors.colors.secondary }}
                            >
                                <i className="fas fa-certification text-2xl mb-2" />
                                <p className="font-bold">{t('template.trainer.nasmCertified')}</p>
                                <p className="text-sm opacity-80">{t('template.trainer.personalTrainer')}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* About */}
            {profile.bio && (
                <section id="about" className="py-24 px-4 bg-white">
                    <div className="max-w-7xl mx-auto">
                        <div className="grid lg:grid-cols-2 gap-16 items-center">
                            <div className="relative">
                                {profile.profile_image ? (
                                    <img
                                        src={profile.profile_image}
                                        alt={profile.business_name}
                                        className="rounded-3xl shadow-2xl w-full object-cover"
                                    />
                                ) : (
                                    <div
                                        className="rounded-3xl shadow-2xl w-full aspect-[6/7] flex items-center justify-center"
                                        style={{ backgroundColor: colors.colors.background }}
                                    >
                                        <span
                                            className="text-6xl font-bold"
                                            style={{ fontFamily: typography.heading, color: colors.colors.primary }}
                                        >
                                            {profile.business_name[0]}
                                        </span>
                                    </div>
                                )}
                                <div
                                    className="absolute -bottom-6 -right-6 rounded-2xl p-6 shadow-xl"
                                    style={{ backgroundColor: colors.colors.primary, color: colors.colors.secondary }}
                                >
                                    <p className="text-4xl font-bold" style={{ fontFamily: typography.heading }}>
                                        12+
                                    </p>
                                    <p className="text-sm">{t('template.trainer.yearsCoaching')}</p>
                                </div>
                            </div>
                            <div>
                                <p className="uppercase tracking-widest mb-4 text-sm font-bold" style={{ color: colors.colors.accent }}>
                                    {t('template.trainer.aboutMe')}
                                </p>
                                <h2
                                    className="text-4xl md:text-5xl font-bold mb-6"
                                    style={{ fontFamily: typography.heading, color: colors.colors.primary }}
                                >
                                    {profile.business_name.toUpperCase()} <span style={{ color: colors.colors.accent }}>{t('template.trainer.willGetYouThere')}</span>
                                </h2>
                                <p className="text-lg mb-6 leading-relaxed" style={{ color: colors.colors.textMuted }}>
                                    {profile.bio}
                                </p>
                                {profile.location && (
                                    <p className="text-sm mb-6" style={{ color: colors.colors.textMuted }}>
                                        📍 {profile.location}
                                    </p>
                                )}
                                <div className="grid grid-cols-2 gap-6 mb-8">
                                    <div
                                        className="rounded-xl p-5"
                                        style={{ backgroundColor: colors.colors.background }}
                                    >
                                        <i className="fas fa-award text-2xl mb-3" style={{ color: colors.colors.accent }} />
                                        <h4 className="font-bold" style={{ color: colors.colors.primary }}>{t('template.trainer.nasmCertified')}</h4>
                                        <p className="text-sm" style={{ color: colors.colors.textMuted }}>{t('template.trainer.personalTrainer')}</p>
                                    </div>
                                    <div
                                        className="rounded-xl p-5"
                                        style={{ backgroundColor: colors.colors.background }}
                                    >
                                        <i className="fas fa-medal text-2xl mb-3" style={{ color: colors.colors.accent }} />
                                        <h4 className="font-bold" style={{ color: colors.colors.primary }}>{t('template.trainer.precisionNutrition')}</h4>
                                        <p className="text-sm" style={{ color: colors.colors.textMuted }}>{t('template.trainer.level1Certified')}</p>
                                    </div>
                                </div>
                                {profile.social_links && profile.social_links.length > 0 && (
                                    <div className="flex items-center space-x-4">
                                        {profile.social_links.slice(0, 3).map((link) => (
                                            <a
                                                key={link.platform}
                                                href={link.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="w-12 h-12 rounded-full flex items-center justify-center transition-all"
                                                style={{ backgroundColor: colors.colors.primary, color: colors.colors.accent }}
                                            >
                                                <i className={`fab fa-${link.platform}`} />
                                            </a>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Services / Programs */}
            {profile.services && profile.services.length > 0 && (
                <section
                    id="services"
                    className="py-24 px-4"
                    style={{ backgroundColor: colors.colors.background }}
                >
                    <div className="max-w-7xl mx-auto">
                        <div className="text-center mb-16">
                            <p className="uppercase tracking-widest mb-4 text-sm font-bold" style={{ color: colors.colors.accent }}>
                                {t('template.trainer.trainingPrograms')}
                            </p>
                            <h2
                                className="text-4xl md:text-5xl font-bold"
                                style={{ fontFamily: typography.heading, color: colors.colors.primary }}
                            >
                                {t('template.trainer.chooseYourPath')}
                            </h2>
                        </div>
                        <div className="grid md:grid-cols-3 gap-8">
                            {profile.services.map((service, index) => (
                                <div
                                    key={service.id}
                                    className={`rounded-3xl p-8 shadow-lg hover:shadow-xl transition-shadow relative overflow-hidden ${index === 1 ? 'scale-105' : ''
                                        }`}
                                    style={
                                        index === 1
                                            ? {
                                                backgroundColor: colors.colors.primary,
                                                color: colors.colors.secondary,
                                            }
                                            : {
                                                backgroundColor: 'white',
                                            }
                                    }
                                >
                                    {index === 1 && (
                                        <div
                                            className="absolute top-0 right-0 px-4 py-1 text-sm font-bold"
                                            style={{ backgroundColor: colors.colors.secondary, color: colors.colors.accent }}
                                        >
                                            {t('template.trainer.bestValue')}
                                        </div>
                                    )}
                                    {index === 0 && (
                                        <div
                                            className="absolute top-0 right-0 px-4 py-1 text-sm font-bold"
                                            style={{ backgroundColor: colors.colors.accent, color: colors.colors.secondary }}
                                        >
                                            {t('template.trainer.popular')}
                                        </div>
                                    )}
                                    <div
                                        className="w-16 h-16 rounded-xl flex items-center justify-center mb-6"
                                        style={{
                                            backgroundColor: index === 1 ? 'rgba(255,255,255,0.1)' : `${colors.colors.accent}10`,
                                        }}
                                    >
                                        <i
                                            className={`fas fa-${index === 0 ? 'dumbbell' : index === 1 ? 'crown' : 'mobile-alt'
                                                } text-2xl`}
                                            style={{ color: colors.colors.accent }}
                                        />
                                    </div>
                                    <h3
                                        className="text-3xl font-bold mb-2"
                                        style={{ fontFamily: typography.heading }}
                                    >
                                        {service.name.toUpperCase()}
                                    </h3>
                                    {service.price_display !== 'hidden' && service.price && (
                                        <p className="text-4xl font-bold mb-4" style={{ color: colors.colors.accent }}>
                                            ${service.price.toFixed(0)}
                                            <span className="text-sm font-normal" style={{ color: index === 1 ? 'rgba(255,255,255,0.7)' : colors.colors.textMuted }}>
                                                /month
                                            </span>
                                        </p>
                                    )}
                                    {service.description && (
                                        <p
                                            className="mb-6"
                                            style={{ color: index === 1 ? 'rgba(255,255,255,0.7)' : colors.colors.textMuted }}
                                        >
                                            {service.description}
                                        </p>
                                    )}
                                    <a
                                        href="#contact"
                                        className="block w-full py-4 rounded-xl font-bold text-center transition-colors"
                                        style={
                                            index === 1
                                                ? {
                                                    backgroundColor: colors.colors.accent,
                                                    color: colors.colors.secondary,
                                                }
                                                : {
                                                    backgroundColor: index === 2 ? colors.colors.primary : colors.colors.accent,
                                                    color: colors.colors.secondary,
                                                }
                                        }
                                    >
                                    {t('template.trainer.getStarted')}
                                    </a>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Testimonials */}
            <section id="testimonials" className="py-24 px-4 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-12">
                        <p className="uppercase tracking-widest mb-4 text-sm font-bold" style={{ color: colors.colors.accent }}>
                            {t('template.trainer.successStories')}
                        </p>
                        <h2
                            className="text-4xl md:text-5xl font-bold"
                            style={{ fontFamily: typography.heading, color: colors.colors.primary }}
                        >
                            REAL RESULTS
                        </h2>
                    </div>
                    <div className="grid md:grid-cols-3 gap-8">
                        <div className="rounded-2xl p-8" style={{ backgroundColor: colors.colors.background }}>
                            <div className="flex mb-4" style={{ color: colors.colors.accent }}>
                                <i className="fas fa-star" />
                                <i className="fas fa-star" />
                                <i className="fas fa-star" />
                                <i className="fas fa-star" />
                                <i className="fas fa-star" />
                            </div>
                            <p className="mb-6 leading-relaxed" style={{ color: colors.colors.textMuted }}>
                                "Lost 30 lbs in 4 months! Coach Mike's program is no joke. The nutrition guidance changed everything
                                for me."
                            </p>
                            <div className="flex items-center space-x-3">
                                <div
                                    className="w-12 h-12 rounded-full flex items-center justify-center"
                                    style={{ backgroundColor: `${colors.colors.accent}20` }}
                                >
                                    <span className="font-bold" style={{ color: colors.colors.accent }}>JW</span>
                                </div>
                                <div>
                                    <p className="font-bold" style={{ color: colors.colors.primary }}>Jason W.</p>
                                    <p className="text-sm" style={{ color: colors.colors.textMuted }}>-30 lbs in 4 months</p>
                                </div>
                            </div>
                        </div>
                        <div className="rounded-2xl p-8" style={{ backgroundColor: colors.colors.background }}>
                            <div className="flex mb-4" style={{ color: colors.colors.accent }}>
                                <i className="fas fa-star" />
                                <i className="fas fa-star" />
                                <i className="fas fa-star" />
                                <i className="fas fa-star" />
                                <i className="fas fa-star" />
                            </div>
                            <p className="mb-6 leading-relaxed" style={{ color: colors.colors.textMuted }}>
                                "As a busy mom, I needed something that worked. Online coaching gave me results without the gym
                                commute!"
                            </p>
                            <div className="flex items-center space-x-3">
                                <div
                                    className="w-12 h-12 rounded-full flex items-center justify-center"
                                    style={{ backgroundColor: `${colors.colors.accent}20` }}
                                >
                                    <span className="font-bold" style={{ color: colors.colors.accent }}>SM</span>
                                </div>
                                <div>
                                    <p className="font-bold" style={{ color: colors.colors.primary }}>Sarah M.</p>
                                    <p className="text-sm" style={{ color: colors.colors.textMuted }}>-25 lbs in 3 months</p>
                                </div>
                            </div>
                        </div>
                        <div className="rounded-2xl p-8" style={{ backgroundColor: colors.colors.background }}>
                            <div className="flex mb-4" style={{ color: colors.colors.accent }}>
                                <i className="fas fa-star" />
                                <i className="fas fa-star" />
                                <i className="fas fa-star" />
                                <i className="fas fa-star" />
                                <i className="fas fa-star" />
                            </div>
                            <p className="mb-6 leading-relaxed" style={{ color: colors.colors.textMuted }}>
                                "Gained 15 lbs of muscle and finally feel confident in my body. Best investment I've made for myself."
                            </p>
                            <div className="flex items-center space-x-3">
                                <div
                                    className="w-12 h-12 rounded-full flex items-center justify-center"
                                    style={{ backgroundColor: `${colors.colors.accent}20` }}
                                >
                                    <span className="font-bold" style={{ color: colors.colors.accent }}>TM</span>
                                </div>
                                <div>
                                    <p className="font-bold" style={{ color: colors.colors.primary }}>Tom M.</p>
                                    <p className="text-sm" style={{ color: colors.colors.textMuted }}>+15 lbs muscle in 5 months</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Gallery */}
            {profile.gallery && profile.gallery.length > 0 && (
                <section className="py-24 px-4" style={{ backgroundColor: colors.colors.background }}>
                    <div className="max-w-7xl mx-auto">
                        <div className="text-center mb-12">
                            <p className="uppercase tracking-widest mb-4 text-sm font-bold" style={{ color: colors.colors.accent }}>
                                {t('template.trainer.gallery')}
                            </p>
                            <h2
                                className="text-4xl md:text-5xl font-bold"
                                style={{ fontFamily: typography.heading, color: colors.colors.primary }}
                            >
                                {t('template.trainer.myWork')}
                            </h2>
                        </div>
                        <Gallery
                            images={profile.gallery}
                            layout="grid"
                            limit={6}
                            enableLightbox
                            aspectRatio="square"
                            gapSize={4}
                        />
                    </div>
                </section>
            )}

            {/* Hours / Availability */}
            {profile.availability && profile.availability.length > 0 && (
                <section className="py-24 px-4" style={{ backgroundColor: colors.colors.background }}>
                    <div className="max-w-4xl mx-auto">
                        <div className="text-center mb-12">
                            <p className="uppercase tracking-widest mb-4 text-sm font-bold" style={{ color: colors.colors.accent }}>
                                {t('template.trainer.availability')}
                            </p>
                            <h2
                                className="text-4xl md:text-5xl font-bold"
                                style={{ fontFamily: typography.heading, color: colors.colors.primary }}
                            >
                                {t('template.trainer.trainingHours')}
                            </h2>
                        </div>
                        <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl">
                            <div className="grid md:grid-cols-2 gap-8">
                                <div className="space-y-4">
                                    {profile.availability.slice(0, 4).map((day) => (
                                        <div
                                            key={day.day_of_week}
                                            className="flex justify-between items-center py-3 border-b"
                                            style={{ borderColor: colors.colors.background }}
                                        >
                                            <span className="font-bold" style={{ color: colors.colors.primary }}>
                                                {day.day_name}
                                            </span>
                                            <span style={{ color: colors.colors.textMuted }}>
                                                {day.is_available
                                                    ? day.is_all_day
                                                        ? t('template.common.allDay')
                                                        : `${day.start_time} - ${day.end_time}`
                                                    : t('template.common.closed')}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                                <div className="space-y-4">
                                    {profile.availability.slice(4).map((day) => (
                                        <div
                                            key={day.day_of_week}
                                            className="flex justify-between items-center py-3 border-b"
                                            style={{ borderColor: colors.colors.background }}
                                        >
                                            <span className="font-bold" style={{ color: colors.colors.primary }}>
                                                {day.day_name}
                                            </span>
                                            <span style={{ color: colors.colors.accent, fontWeight: 'bold' }}>
                                                {day.is_available
                                                    ? day.is_all_day
                                                        ? t('template.common.allDay')
                                                        : `${day.start_time} - ${day.end_time}`
                                                    : t('template.common.closed')}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="mt-8 text-center rounded-xl p-6" style={{ backgroundColor: `${colors.colors.accent}10` }}>
                                <p style={{ color: colors.colors.text }}>
                                    <i className="fas fa-info-circle mr-2" style={{ color: colors.colors.accent }} />
                                    <strong>{t('template.trainer.onlineCoaching247')}</strong>
                                </p>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Contact */}
            <section id="contact" className="py-24 px-4" style={{ backgroundColor: colors.colors.primary, color: colors.colors.secondary }}>
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-16">
                        <div>
                            <p className="uppercase tracking-widest mb-4 text-sm font-bold" style={{ color: colors.colors.accent }}>
                                {t('template.trainer.getStartedSection')}
                            </p>
                            <h2
                                className="text-4xl md:text-5xl font-bold mb-6"
                                style={{ fontFamily: typography.heading }}
                            >
                                {t('template.trainer.readyToTransform')}
                            </h2>
                            <p className="text-lg mb-8 leading-relaxed" style={{ color: 'rgba(255,255,255,0.7)' }}>
                                {t('template.trainer.freeConsultationText')}
                            </p>
                            <div className="space-y-6">
                                {profile.email && (
                                    <div className="flex items-start space-x-4">
                                        <div
                                            className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                                            style={{ backgroundColor: `${colors.colors.accent}20` }}
                                        >
                                            <i className="fas fa-envelope text-xl" style={{ color: colors.colors.accent }} />
                                        </div>
                                        <div>
                                            <h4 className="font-bold mb-1">{t('template.trainer.email')}</h4>
                                            <p style={{ color: 'rgba(255,255,255,0.7)' }}>{profile.email}</p>
                                        </div>
                                    </div>
                                )}
                                {profile.phone && (
                                    <div className="flex items-start space-x-4">
                                        <div
                                            className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                                            style={{ backgroundColor: `${colors.colors.accent}20` }}
                                        >
                                            <i className="fas fa-phone text-xl" style={{ color: colors.colors.accent }} />
                                        </div>
                                        <div>
                                            <h4 className="font-bold mb-1">{t('template.trainer.phone')}</h4>
                                            <p style={{ color: 'rgba(255,255,255,0.7)' }}>{profile.phone}</p>
                                        </div>
                                    </div>
                                )}
                                {profile.location && (
                                    <div className="flex items-start space-x-4">
                                        <div
                                            className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                                            style={{ backgroundColor: `${colors.colors.accent}20` }}
                                        >
                                            <i className="fas fa-map-marker-alt text-xl" style={{ color: colors.colors.accent }} />
                                        </div>
                                        <div>
                                            <h4 className="font-bold mb-1">{t('template.trainer.location')}</h4>
                                            <p style={{ color: 'rgba(255,255,255,0.7)' }}>{profile.location}</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                            {profile.social_links && profile.social_links.length > 0 && (
                                <div className="mt-8">
                                    <h4 className="font-bold mb-4">{t('template.trainer.followMyJourney')}</h4>
                                    <div className="flex space-x-4">
                                        {profile.social_links.slice(0, 3).map((link) => (
                                            <a
                                                key={link.platform}
                                                href={link.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="w-14 h-14 rounded-xl flex items-center justify-center transition-all"
                                                style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
                                            >
                                                <i className={`fab fa-${link.platform} text-xl`} style={{ color: colors.colors.accent }} />
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div>
                            <form
                                className="rounded-3xl p-8 shadow-xl"
                                style={{ backgroundColor: colors.colors.secondary, color: colors.colors.text }}
                            >
                                <h3
                                    className="text-2xl font-bold mb-6"
                                    style={{ fontFamily: typography.heading, color: colors.colors.primary }}
                                >
                                    {t('template.trainer.freeConsultation')}
                                </h3>
                                <div className="space-y-5">
                                    <div>
                                        <label className="block text-sm font-medium mb-2" style={{ color: colors.colors.primary }}>
                                            {t('template.trainer.name')}
                                        </label>
                                        <input
                                            type="text"
                                            className="w-full px-4 py-4 rounded-xl border outline-none transition-all"
                                            style={{
                                                borderColor: colors.colors.background,
                                                backgroundColor: 'white',
                                            }}
                                            placeholder={t('template.trainer.yourName')}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-2" style={{ color: colors.colors.primary }}>
                                            {t('template.trainer.email')}
                                        </label>
                                        <input
                                            type="email"
                                            className="w-full px-4 py-4 rounded-xl border outline-none transition-all"
                                            style={{
                                                borderColor: colors.colors.background,
                                                backgroundColor: 'white',
                                            }}
                                            placeholder="your@email.com"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-2" style={{ color: colors.colors.primary }}>
                                            {t('template.trainer.phone')}
                                        </label>
                                        <input
                                            type="tel"
                                            className="w-full px-4 py-4 rounded-xl border outline-none transition-all"
                                            style={{
                                                borderColor: colors.colors.background,
                                                backgroundColor: 'white',
                                            }}
                                            placeholder="(555) 123-4567"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-2" style={{ color: colors.colors.primary }}>
                                            {t('template.trainer.interestedIn')}
                                        </label>
                                        <select
                                            className="w-full px-4 py-4 rounded-xl border outline-none transition-all cursor-pointer"
                                            style={{
                                                borderColor: colors.colors.background,
                                                backgroundColor: 'white',
                                            }}
                                        >
                                            <option value="">{t('template.trainer.selectAProgram')}</option>
                                            {profile.services && profile.services.length > 0 ? (
                                                profile.services.map((service) => (
                                                    <option key={service.id} value={service.id}>
                                                        {service.name} - ${service.price?.toFixed(0)}/mo
                                                    </option>
                                                ))
                                            ) : (
                                                <>
                                                    <option>Online Coaching - $197/mo</option>
                                                    <option>1:1 Training - $600/mo</option>
                                                    <option>App Only - $49/mo</option>
                                                </>
                                            )}
                                            <option>{t('template.trainer.notSureYet')}</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-2" style={{ color: colors.colors.primary }}>
                                            {t('template.trainer.yourGoals')}
                                        </label>
                                        <textarea
                                            rows={4}
                                            className="w-full px-4 py-4 rounded-xl border outline-none transition-all resize-none"
                                            style={{
                                                borderColor: colors.colors.background,
                                                backgroundColor: 'white',
                                            }}
                                            placeholder={t('template.trainer.tellMeAboutYourFitnessGoals')}
                                        />
                                    </div>
                                    <button
                                        type="submit"
                                        className="w-full py-4 rounded-xl font-bold transition-all"
                                        style={{
                                            backgroundColor: colors.colors.accent,
                                            color: colors.colors.secondary,
                                        }}
                                    >
                                        {t('template.trainer.getMyFreeConsultation')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer
                className="py-12 px-4 text-center border-t"
                style={{ backgroundColor: colors.colors.primary, color: colors.colors.secondary, borderColor: 'rgba(255,255,255,0.1)' }}
            >
                <div
                    className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                    style={{ backgroundImage: `linear-gradient(to right, ${colors.colors.accent}, ${colors.colors.secondary})` }}
                >
                    <i className="fas fa-dumbbell text-white text-2xl" />
                </div>
                <h3 className="text-2xl font-bold tracking-wide mb-2" style={{ fontFamily: typography.heading }}>
                    {profile.business_name.toUpperCase()}
                </h3>
                <p className="mb-6" style={{ color: colors.colors.secondary, opacity: 0.6 }}>
                    {t('template.trainer.personalTrainingAndOnlineCoaching')}
                </p>
                {profile.social_links && profile.social_links.length > 0 && (
                    <div className="flex justify-center space-x-4 mb-6">
                        {profile.social_links.slice(0, 3).map((link) => (
                            <a
                                key={link.platform}
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-10 h-10 rounded-full flex items-center justify-center transition-all"
                                style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
                            >
                                <i className={`fab fa-${link.platform}`} />
                            </a>
                        ))}
                    </div>
                )}
                <p className="text-sm" style={{ color: colors.colors.secondary, opacity: 0.4 }}>
                    © {new Date().getFullYear()} {profile.business_name}. {t('template.trainer.allRightsReserved')}
                </p>
            </footer>

            {isBookingOpen && profile.services && profile.services.length > 0 && (
                <BookingModal
                    isOpen={isBookingOpen}
                    onClose={() => setIsBookingOpen(false)}
                    profile={profile}
                    services={profile.services}
                    categories={[]}
                />
            )}
        </div>
    )
}

export default PersonalTrainerTemplate;
